import { useState, useCallback, useRef } from 'react';
import type { HistoryState, CanvasObject } from '../types';

const MAX_HISTORY = 100;

/**
 * 히스토리 관리 훅
 * 백업 코드 방식을 기반으로 구현
 */
export function useHistory() {
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const historyIndexRef = useRef(-1);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  /**
   * 히스토리에 상태 저장
   */
  const saveToHistory = useCallback((state: HistoryState) => {
    // ref를 먼저 업데이트하여 다음 saveToHistory 호출 시 올바른 인덱스를 사용
    const currentIndex = historyIndexRef.current;
    const newIndex = currentIndex + 1;
    historyIndexRef.current = newIndex;
    
    setHistory(prevHistory => {
      const newHistory = prevHistory.slice(0, currentIndex + 1);
      
      // 깊은 복사로 히스토리 저장
      // Map을 배열로 변환하여 직렬화
      const serializedObjects = Array.from(state.objects.entries()).map(([id, obj]: [string, CanvasObject]) => {
        // 이미지 객체는 image 요소를 제외하고 직렬화 (imageHash로 복원)
        if (obj.type === 'image' && obj.image) {
          const serialized = JSON.parse(JSON.stringify(obj));
          return {
            ...serialized,
            image: undefined, // HTMLImageElement는 직렬화 불가, imageHash로 복원
            imageSrc: obj.imageSrc // imageSrc는 유지
          };
        }
        return JSON.parse(JSON.stringify(obj));
      });
      
      // Map으로 다시 변환
      const objectsMap = new Map<string, CanvasObject>(serializedObjects.map((obj: any) => [obj.id, obj]));
      
      newHistory.push({
        objects: objectsMap,
        zOrder: [...state.zOrder],
        selectedId: state.selectedId
      });
      
      // 최대 히스토리 수 제한
      let shouldAdjustIndex = false;
      if (newHistory.length > MAX_HISTORY) {
        newHistory.shift();
        shouldAdjustIndex = true;
      }
      
      // 인덱스 조정
      if (shouldAdjustIndex) {
        historyIndexRef.current = MAX_HISTORY - 1;
        setHistoryIndex(MAX_HISTORY - 1);
      } else {
        setHistoryIndex(newIndex);
      }
      
      return newHistory;
    });
  }, []);

  /**
   * 되돌리기
   */
  const undo = useCallback((): HistoryState | null => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      // ref를 먼저 업데이트하여 다음 호출 시 최신 값을 사용
      historyIndexRef.current = newIndex;
      setHistoryIndex(newIndex);
      return history[newIndex];
    }
    return null;
  }, [history, historyIndex]);

  /**
   * 다시 실행
   */
  const redo = useCallback((): HistoryState | null => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      // ref를 먼저 업데이트하여 다음 호출 시 최신 값을 사용
      historyIndexRef.current = newIndex;
      setHistoryIndex(newIndex);
      return history[newIndex];
    }
    return null;
  }, [history, historyIndex]);

  /**
   * 히스토리 초기화
   */
  const clearHistory = useCallback(() => {
    setHistory([]);
    setHistoryIndex(-1);
    historyIndexRef.current = -1;
  }, []);

  return {
    saveToHistory,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory
  };
}

